<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';
import type { TemplateFilter } from '@/api';
import { IconFormatting } from '@/components/design/icons';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import { gaiaFontSize, gaiaFonts, gaiaTemplating } from '@/components/design/themes/tokens';

/**
 * The template's output filters, behind a small wand beside the field's label
 * — the same idiom as the "?" guidance hint next to it. Hovering lists the
 * filters as chips in the violet the document preview gives its template
 * tags, one per filter with its arguments ("format_date · de · short"). The
 * form itself stays clean; the fact is a hover away. Renders nothing when the
 * field has no filters.
 */
interface Props {
  filters?: TemplateFilter[];
}

const props = defineProps<Props>();

const chips = computed<string[]>(() =>
  (props.filters ?? []).map(filter =>
    [
      filter.name,
      ...(filter.args ?? []).filter(
        (arg): arg is string => typeof arg === 'string' && arg.length > 0
      ),
    ].join(' · ')
  )
);

/** A bare wand in the template violet: minimal, but not grey — the fact it points at matters. */
const triggerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  color: gaiaTemplating.customFg,
  cursor: 'help',
};

// Inline, not scoped CSS: the tooltip teleports to <body>, where a
// component-root `v-bind()` would never reach these chips.
const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: '20px',
  padding: '0 8px',
  border: `1px solid ${gaiaTemplating.customBorder}`,
  borderRadius: '6px',
  background: gaiaTemplating.customSurface,
  color: gaiaTemplating.customFg,
  fontFamily: gaiaFonts.mono,
  fontSize: gaiaFontSize.nano,
  lineHeight: 1,
  whiteSpace: 'nowrap',
};
</script>

<template>
  <gaia-tooltip v-if="chips.length > 0" placement="top" :max-width="360">
    <template #trigger>
      <span role="img" :aria-label="`Formatting: ${chips.join(', ')}`" :style="triggerStyle">
        <n-icon :size="14"><icon-formatting /></n-icon>
      </span>
    </template>
    <n-flex vertical :size="6">
      <gaia-text size="micro" tone="subtle">Formatting in the document</gaia-text>
      <n-flex :size="6">
        <span v-for="(chip, index) in chips" :key="index" :style="chipStyle">{{ chip }}</span>
      </n-flex>
    </n-flex>
  </gaia-tooltip>
</template>
